import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../../features/userSlice";
import { LoadingState } from "../../../components/UI/States";
import { branches as BUILT_IN_BRANCHES } from "../Utilities/api";
import { fetchAccessOverview, fetchAccessUsers, fetchAutomation } from "./accessApi";
import { buildPageCatalog, buildRoleCatalog } from "./accessCatalog";
import { Notice } from "./accessUi";
import DirectorySyncCard from "./DirectorySyncCard";
import AutoRolesCard from "./AutoRolesCard";

// User Manager → Automation: the Microsoft 365 directory sync and automatic
// roles (server: lib/directorySync.js, lib/autoRoles.js).
const UserAutomation = () => {
  const actor = useSelector(selectUser);
  const [automation, setAutomation] = useState(null);
  const [users, setUsers] = useState([]);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState(null);

  const pages = useMemo(() => buildPageCatalog(), []);

  const load = useCallback(async () => {
    if (!actor?.id) return;
    setError(null);
    try {
      const [nextAutomation, list, nextOverview] = await Promise.all([
        fetchAutomation(actor.id),
        fetchAccessUsers(actor.id),
        fetchAccessOverview(actor.id),
      ]);
      setAutomation(nextAutomation);
      setUsers(list.users || []);
      setOverview(nextOverview);
    } catch (err) {
      setError(err.message || "Couldn't load automation settings.");
    }
  }, [actor?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const roleCatalog = useMemo(
    () => buildRoleCatalog({ customRoles: overview?.customRoles, users, pages }),
    [overview, users, pages]
  );
  const branchOptions = useMemo(
    () =>
      [...new Set([...BUILT_IN_BRANCHES, ...users.map((u) => u.branch)])]
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [users]
  );

  if (error) return <Notice message={{ type: "error", text: error }} />;
  if (!automation) return <LoadingState label="Loading automation…" />;

  return (
    <div className="space-y-6">
      <DirectorySyncCard directory={automation.directory} users={users} actorId={actor?.id} onChanged={load} />
      <AutoRolesCard
        autoRoles={automation.autoRoles}
        roleCatalog={roleCatalog}
        branchOptions={branchOptions}
        actorId={actor?.id}
        onChanged={load}
      />
    </div>
  );
};

export default UserAutomation;
